/**
 * Act3Screen — 第三幕主屏幕
 */
import React, { useEffect, useRef } from 'react';
import { Act3TopBar } from './Act3TopBar';
import { Act3TrialReveal } from './Act3TrialReveal';
import { Act3StrategySelect } from './Act3StrategySelect';
import { Act3WeekResult } from './Act3WeekResult';
import { useAct3Store } from '../../stores/act3Store';
import { useRootStore } from '../../stores/rootStore';
import { useAct3Loop } from '../../hooks/useAct3Loop';
import { Loader2 } from 'lucide-react';

interface Act3ScreenProps {
  onSettingsClick?: () => void;
}

export const Act3Screen: React.FC<Act3ScreenProps> = ({ onSettingsClick }) => {
  const root = useRootStore();
  const act3 = useAct3Store();
  const { initAct3, confirmTrial, executeWeek, confirmWeekResult, phase } = useAct3Loop();
  const hasInit = useRef(false);

  useEffect(() => {
    if (!hasInit.current && act3.phase === 'WAITING' && act3.trials.length === 0) {
      hasInit.current = true;
      initAct3();
    }
  }, [act3.phase, act3.trials.length, initAct3]);

  if (!root.protagonistA || !root.protagonistB) return null;

  const activeTrial = act3.trials[act3.activeTrialIndex];

  return (
    <div className="h-screen flex flex-col bg-cream">
      <Act3TopBar
        week={act3.week}
        trialName={activeTrial?.name ?? ''}
        trialCategory={activeTrial?.category ?? ''}
        commitmentA={act3.commitmentA}
        commitmentB={act3.commitmentB}
        energyA={act3.energyA}
        energyB={act3.energyB}
        stability={act3.stability}
        nameA={root.protagonistA.name}
        nameB={root.protagonistB.name}
        onSettingsClick={onSettingsClick}
      />

      {/* Phase-based content */}
      {phase === 'TRIAL_REVEAL' && activeTrial && (
        <Act3TrialReveal
          trial={activeTrial}
          weekNumber={act3.week}
          onConfirm={confirmTrial}
        />
      )}

      {phase === 'STRATEGY_SELECT' && activeTrial && (
        <Act3StrategySelect
          trial={activeTrial}
          energyA={act3.energyA}
          energyB={act3.energyB}
          usedSpecials={act3.usedSpecialStrategies}
          entryType={act3.entryType}
          onSelect={executeWeek}
        />
      )}

      {phase === 'PROCESSING' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="text-coral-500 animate-spin" />
            <span className="text-sm text-[#9CA3AF]">命运的齿轮正在转动...</span>
          </div>
        </div>
      )}

      {phase === 'WEEK_RESULT' && (
        <Act3WeekResult
          week={act3.week}
          narrative={act3.weekNarrative}
          commitmentA={act3.commitmentA}
          commitmentB={act3.commitmentB}
          energyA={act3.energyA}
          energyB={act3.energyB}
          stability={act3.stability}
          nameA={root.protagonistA.name}
          nameB={root.protagonistB.name}
          onContinue={confirmWeekResult}
          innerMonologueA={act3.innerMonologueA}
          innerMonologueB={act3.innerMonologueB}
          streamingMonologueSide={act3.streamingMonologueSide}
          streamingMonologueText={act3.streamingMonologueText}
        />
      )}

      {phase === 'FINISHED' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="text-coral-500 animate-spin" />
            <span className="text-sm text-[#9CA3AF]">正在生成最终结局...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Act3Screen;
